import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createVehicle,
  deleteVehicle,
  listVehicles,
  updateVehicle,
  type VehicleItem,
} from "@/lib/api";

const STATUSES = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
  { value: "maintenance", label: "En mantención" },
];

const statusLabel = (s: string) => STATUSES.find((x) => x.value === s)?.label ?? s;

type FormState = { code: string; name: string; plate: string; status: string };
const emptyForm: FormState = { code: "", name: "", plate: "", status: "active" };

export default function VehiculosTab() {
  const { session } = useSession();
  const [items, setItems] = useState<VehicleItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listVehicles(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar vehículos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [session]);

  function openCreate(): void {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setShowModal(true);
  }

  function openEdit(item: VehicleItem): void {
    setEditingId(item.id);
    setForm({ code: item.code, name: item.name, plate: item.plate, status: item.status });
    setError(null);
    setShowModal(true);
  }

  function closeModal(): void {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
  }

  async function onSave(): Promise<void> {
    if (!session || !form.name.trim() || !form.plate.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        const updated = await updateVehicle(session, editingId, { name: form.name.trim(), plate: form.plate.trim().toUpperCase(), status: form.status });
        setItems((prev) => prev.map((i) => (i.id === editingId ? updated : i)));
      } else {
        if (!form.code.trim()) return;
        const created = await createVehicle(session, { code: form.code.trim().toUpperCase(), name: form.name.trim(), plate: form.plate.trim().toUpperCase(), status: form.status });
        setItems((prev) => [...prev, created]);
      }
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item: VehicleItem): Promise<void> {
    if (!session || !window.confirm(`¿Eliminar vehículo "${item.name} (${item.plate})"?`)) return;
    setError(null);
    try {
      await deleteVehicle(session, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar");
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Vehículos ({items.length})</h2>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <i className="ri-add-line" />
            Nuevo vehículo
          </button>
        </div>

        {loading ? (
          <div className="h-16 bg-gray-100 rounded animate-pulse" />
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">Sin vehículos registrados.</p>
            ) : (
              items.map((item) => (
                <div key={item.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-800">{item.name}</div>
                    <div className="text-xs text-gray-400">
                      <span className="font-mono">{item.plate}</span>
                      {" · "}
                      <span className="font-mono text-gray-300">{item.code}</span>
                      {" · "}
                      <span className={item.status === "active" ? "text-green-600" : "text-orange-500"}>{statusLabel(item.status)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(item)} className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">
                      Editar
                    </button>
                    <button
                      onClick={() => void onDelete(item)}
                      className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">{editingId ? "Editar vehículo" : "Nuevo vehículo"}</h3>
              <button onClick={closeModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-3">
              {!editingId && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Código interno</label>
                  <input
                    value={form.code}
                    onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                    placeholder="Ej: VEH-001"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:border-blue-400"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nombre / descripción</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Ej: Camioneta Toyota Hilux"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Patente</label>
                <input
                  value={form.plate}
                  onChange={(e) => setForm((p) => ({ ...p, plate: e.target.value.toUpperCase() }))}
                  placeholder="Ej: ABCD12"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={() => void onSave()}
                disabled={saving || !form.name.trim() || !form.plate.trim() || (!editingId && !form.code.trim())}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60 hover:bg-blue-700"
              >
                {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear vehículo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
